// src/components/NotificacoesVagas.jsx
// Notificações de vagas na cidade do candidato (popup lateral direito)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, X, Briefcase, DollarSign } from 'lucide-react';
import api from '../utils/api';
import useAuthStore from '../store/authStore';

// Intervalo de polling em ms (30 segundos)
const POLLING_INTERVAL = 30000;
// Duração de cada notificação em ms (5 segundos)
const NOTIF_DURATION = 5000;
// Máximo de notificações visíveis ao mesmo tempo
const MAX_VISIVEIS = 3;

let idCounter = 0;
function gerarId() { return ++idCounter; }

export default function NotificacoesVagas() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const vagasVistasRef = useRef(new Set());
  const cidadeRef = useRef(null);
  const timerRefs = useRef({});

  // Obtém a cidade — primeiro pelo perfil da API, depois pelo geolocation (apenas para log/fallback)
  useEffect(() => {
    if (!user) return;

    // Busca cidade do perfil (mais confiável)
    api.get('/candidatos/perfil')
      .then(r => {
        if (r.data?.cidade) {
          cidadeRef.current = r.data.cidade;
        }
      })
      .catch(() => {});

    // Tenta geolocalização como informação adicional (não bloqueante)
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        () => {
          // Posição obtida — cidade já vem do perfil cadastrado (Acre)
          // Geolocation confirma que o usuário está ativo
        },
        () => {
          // Negado ou erro — usa cidade do perfil mesmo
        },
        { timeout: 8000, maximumAge: 300000 }
      );
    }
  }, [user]);

  const mostrarNotificacao = useCallback((vaga) => {
    const id = gerarId();
    setNotifs(prev => {
      const novas = [...prev, { ...vaga, _notifId: id }];
      // Mantém só as MAX_VISIVEIS mais recentes
      return novas.slice(-MAX_VISIVEIS);
    });

    // Auto-dismiss após NOTIF_DURATION
    timerRefs.current[id] = setTimeout(() => {
      setNotifs(prev => prev.filter(n => n._notifId !== id));
      delete timerRefs.current[id];
    }, NOTIF_DURATION);
  }, []);

  const fecharNotif = useCallback((id) => {
    clearTimeout(timerRefs.current[id]);
    delete timerRefs.current[id];
    setNotifs(prev => prev.filter(n => n._notifId !== id));
  }, []);

  const abrirVaga = useCallback((notif) => {
    fecharNotif(notif._notifId);
    // Navega para matches com a vaga em destaque, ou para a lista pública de vagas
    navigate('/candidato/matches', { state: { vagaDestaque: notif.id } });
  }, [fecharNotif, navigate]);

  // Polling de novas vagas (notifica somente as novas após o carregamento inicial)
  useEffect(() => {
    if (!user) return;

    let montado = true;
    let primeiraVez = true;
    let intervaloId = null;

    const poll = async () => {
      if (!montado) return;
      const cidade = cidadeRef.current;
      if (!cidade) return;

      try {
        const { data } = await api.get(`/vagas?cidade=${encodeURIComponent(cidade)}&limit=5`);
        const vagas = data.vagas || [];

        if (primeiraVez) {
          // Primeira carga: apenas registra os IDs existentes sem notificar
          vagas.forEach(v => vagasVistasRef.current.add(v.id));
          primeiraVez = false;
        } else {
          // Cargas subsequentes: notifica apenas as vagas novas
          vagas.forEach(v => {
            if (!vagasVistasRef.current.has(v.id)) {
              vagasVistasRef.current.add(v.id);
              mostrarNotificacao(v);
            }
          });
        }
      } catch {
        // Silencioso
      }
    };

    // Aguarda 3s para a cidade ser carregada do perfil, depois inicia o polling
    const iniciarTimeout = setTimeout(() => {
      if (!montado) return;
      poll(); // primeiro poll
      intervaloId = setInterval(poll, POLLING_INTERVAL);
    }, 3000);

    return () => {
      montado = false;
      clearTimeout(iniciarTimeout);
      if (intervaloId) clearInterval(intervaloId);
      // Limpa todos os timers de notificação
      Object.values(timerRefs.current).forEach(clearTimeout);
    };
  }, [user, mostrarNotificacao]);

  if (notifs.length === 0) return null;

  return (
    <div
      className="fixed bottom-6 right-4 z-50 flex flex-col gap-2 items-end"
      style={{ maxWidth: '320px' }}
      aria-live="polite"
      aria-label="Notificações de vagas"
    >
      {notifs.map(notif => (
        <NotifCard
          key={notif._notifId}
          notif={notif}
          onFechar={() => fecharNotif(notif._notifId)}
          onAbrir={() => abrirVaga(notif)}
          duracao={NOTIF_DURATION}
        />
      ))}
    </div>
  );
}

// ─── Card de notificação individual ──────────────────────────
function NotifCard({ notif, onFechar, onAbrir, duracao }) {
  const [saindo, setSaindo] = useState(false);
  const [progresso, setProgresso] = useState(100);

  // Barra de progresso para o auto-dismiss
  useEffect(() => {
    const inicio = Date.now();
    const frame = () => {
      const decorrido = Date.now() - inicio;
      const restante = Math.max(0, 100 - (decorrido / duracao) * 100);
      setProgresso(restante);
      if (restante > 0) requestAnimationFrame(frame);
    };
    const raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [duracao]);

  // Animação de saída
  const fechar = () => {
    setSaindo(true);
    setTimeout(onFechar, 200);
  };

  const salario = notif.salario_min
    ? `R$ ${Number(notif.salario_min).toLocaleString('pt-BR')}`
    : null;

  return (
    <div
      className={`relative w-80 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden cursor-pointer
        transition-all duration-200
        ${saindo ? 'opacity-0 translate-x-8' : 'opacity-100 translate-x-0'}`}
      style={{ animation: saindo ? undefined : 'slideInRight 0.25s ease-out' }}
      onClick={onAbrir}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onAbrir()}
      aria-label={`Nova vaga: ${notif.titulo}`}
    >
      {/* Barra de progresso no topo */}
      <div className="h-1 bg-gray-100">
        <div
          className="h-full bg-gradient-to-r from-acre-green to-acre-teal transition-none"
          style={{ width: `${progresso}%` }}
        />
      </div>

      <div className="p-3.5">
        {/* Cabeçalho */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5 text-xs text-acre-green font-semibold">
            <MapPin size={11} />
            <span>Nova vaga em {notif.cidade_vaga || notif.cidade}</span>
          </div>
          <button
            type="button"
            onClick={e => { e.stopPropagation(); fechar(); }}
            className="w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-400 flex-shrink-0 transition-colors"
            aria-label="Fechar notificação"
          >
            <X size={10} />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-acre-green to-acre-teal flex items-center justify-center flex-shrink-0">
            <Briefcase size={16} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-sora font-bold text-sm text-acre-dark truncate">{notif.titulo}</p>
            <p className="text-xs text-gray-500 truncate">
              {notif.nome_fantasia || notif.razao_social || notif.empresa || 'Empresa'}
            </p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {notif.modalidade && (
                <span className="inline-flex items-center gap-0.5 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full capitalize">
                  {notif.modalidade}
                </span>
              )}
              {salario && (
                <span className="inline-flex items-center gap-0.5 text-xs bg-green-50 text-green-700 font-semibold px-2 py-0.5 rounded-full">
                  <DollarSign size={9} />{salario}
                </span>
              )}
            </div>
          </div>
        </div>

        <p className="mt-2.5 text-xs text-acre-green font-semibold text-right">
          Clique para ver a vaga →
        </p>
      </div>
    </div>
  );
}
